import { FC } from "react";
import { useBasket, useBasketDispatch } from "../basketContext";
import { useSelector } from "react-redux";
import BasketReducerActions from "../enums/BasketReducerActions";
import { IWebTicket } from "app/WebEvent/interfaces";
import {
    Box,
    Checkbox,
    FormControlLabel,
    Icon,
    IconButton,
    Typography,
} from "@mui/material";
import getSubtranslation from "helpers/getSubtranslation";
import Availability from "./Availability";
import { TicketUpsales } from "../TicketUpsales";

type CheckboxTicketProps = {
    ticket: IWebTicket;
    quantity: number;
};

const CheckboxTicket: FC<CheckboxTicketProps> = ({ ticket, quantity }) => {
    const { maxTickets, totalTickets } = useBasket();
    const dispatch = useBasketDispatch();

    const { "general-currency": currency } = useSelector(
        (state: any) => state.web.settings.location_settings,
    );

    const handleSelectTicket = () => {
        dispatch?.({
            type: BasketReducerActions.CHANGE_TICKET_QUANTITY,
            payload: { ticket, quantity: quantity > 0 ? 0 : 1 },
        });
    };

    const handleMultiTicketRemoveQuantity = () => {
        dispatch?.({
            type: BasketReducerActions.CHANGE_TICKET_QUANTITY,
            payload: { ticket, quantity: quantity - 1 },
        });
    };

    const handleMultiTicketAddQuantity = () => {
        dispatch?.({
            type: BasketReducerActions.CHANGE_TICKET_QUANTITY,
            payload: { ticket, quantity: quantity + 1 },
        });
    };

    const renderQuantity = () => {
        return (
            <Box
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                }}
            >
                <Box display="flex" justifyContent="start" alignItems="center">
                    <IconButton
                        onClick={handleMultiTicketRemoveQuantity}
                        sx={{
                            ":hover": {
                                boxShadow: "none",
                            },
                            width: { xs: 24, tablet: 28 },
                            height: { xs: 24, tablet: 28 },
                            "&.Mui-disabled": {
                                backgroundColor: "rgb(185 121 100 / 0.26)",
                            },
                        }}
                    >
                        <Icon sx={{ fontSize: { xs: 16, tablet: 18 } }}>
                            remove
                        </Icon>
                    </IconButton>
                    <Typography
                        sx={{
                            fontSize: { xs: 14, tablet: 16 },
                            fontWeight: 500,
                            px: 2,
                            fontFamily: "'Noto Sans', sans-serif",
                            color: {
                                xs: "#495366",
                                tablet: "#02263A",
                            },
                            width: "auto !important",
                            minWidth: 55,
                            textAlign: "center",
                        }}
                    >
                        {quantity}
                    </Typography>

                    <IconButton
                        onClick={handleMultiTicketAddQuantity}
                        disabled={
                            totalTickets === maxTickets ||
                            quantity === ticket.current_available
                        }
                        sx={{
                            ":hover": {
                                boxShadow: "none",
                            },
                            width: { xs: 24, tablet: 28 },
                            height: { xs: 24, tablet: 28 },
                        }}
                    >
                        <Icon sx={{ fontSize: { xs: 16, tablet: 18 } }}>
                            add
                        </Icon>
                    </IconButton>
                </Box>
            </Box>
        );
    };

    return (
        <>
            <FormControlLabel
                key={`ticket-${ticket.id}`}
                value={ticket.id}
                control={
                    <Checkbox
                        onChange={handleSelectTicket}
                        checked={quantity > 0}
                        size="small"
                        disableRipple
                        disabled={ticket.current_available === 0}
                        sx={{
                            pt: "2px",
                        }}
                    />
                }
                sx={{
                    mr: 0,
                    color: "#495366",
                    alignItems: "flex-start",
                    ".MuiTypography-root": {
                        width: "100%",
                        fontSize: {
                            xs: 14,
                            tablet: 16,
                        },
                    },
                }}
                label={
                    <Box>
                        <Box
                            onClick={(e) => e.preventDefault()}
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 1.5,
                                rowGap: 1,
                                width: "100%",
                                fontFamily: "'Noto Sans', sans-serif",
                                fontSize: {
                                    xs: 14,
                                    tablet: 16,
                                },
                                fontWeight: 500,
                                cursor: "default",
                            }}
                        >
                            <Box
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    wordBreak: "break-word",
                                    color: {
                                        xs: "#495366",
                                        tablet: "#02263A",
                                    },
                                    width: "fit-content",
                                    cursor: "pointer",
                                }}
                            >
                                {getSubtranslation(ticket.subtranslations.name)}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "end",
                                    color: {
                                        xs: "#495366",
                                        tablet: "#02263A",
                                    },
                                    ml: 1,
                                    whiteSpace: "nowrap",
                                    cursor: "default",
                                }}
                            >
                                {ticket.price.price(currency, "left")}
                            </Box>
                        </Box>
                        <Box
                            onClick={(e) => e.preventDefault()}
                            sx={{
                                minWidth: "132px",
                                mt: 1,
                                display: "flex",
                                flexDirection: "row",
                                gap: 2,
                                flexWrap: "wrap",
                                alignItems: "center",
                                cursor: "default",
                            }}
                        >
                            {
                                <Availability
                                    ticket={ticket}
                                    quantity={quantity}
                                />
                            }
                            {quantity > 0 && renderQuantity()}
                        </Box>
                    </Box>
                }
            />
            {quantity > 0 && <TicketUpsales ticket={ticket} />}
        </>
    );
};

export default CheckboxTicket;
